/**
 * TalentLens AI - Shared Constants
 * Application-wide constants for validation, configuration, and enums
 */

// ============================================================================
// User Roles
// ============================================================================

export const USER_ROLES = {
  ADMIN: 'admin',
  RECRUITER: 'recruiter',
  HIRING_MANAGER: 'hiring_manager',
  VIEWER: 'viewer',
} as const;

export const ROLE_PERMISSIONS = {
  [USER_ROLES.ADMIN]: [
    'cv.upload',
    'cv.view',
    'cv.delete',
    'cv.download',
    'cv.archive',
    'job.create',
    'job.edit',
    'job.delete',
    'search.execute',
    'search.export',
    'match.execute',
    'analytics.view',
    'user.manage',
  ],
  [USER_ROLES.RECRUITER]: [
    'cv.upload',
    'cv.view',
    'cv.download',
    'job.create',
    'job.edit',
    'search.execute',
    'search.export',
    'match.execute',
    'analytics.view',
  ],
  [USER_ROLES.HIRING_MANAGER]: [
    'cv.view',
    'cv.download',
    'search.execute',
    'match.execute',
    'analytics.view',
  ],
  [USER_ROLES.VIEWER]: [
    'cv.view',
    'search.execute',
  ],
} as const;

// ============================================================================
// CV Status
// ============================================================================

export const CV_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  PROCESSED: 'processed',
  FAILED: 'failed',
  PARTIAL: 'partial',
} as const;

export const CV_STATUS_LABELS = {
  [CV_STATUS.PENDING]: 'Pending',
  [CV_STATUS.PROCESSING]: 'Processing',
  [CV_STATUS.PROCESSED]: 'Processed',
  [CV_STATUS.FAILED]: 'Failed',
  [CV_STATUS.PARTIAL]: 'Partially Processed',
} as const;

// ============================================================================
// Job Priority
// ============================================================================

export const JOB_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
} as const;

export const JOB_PRIORITY_LABELS = {
  [JOB_PRIORITY.LOW]: 'Low',
  [JOB_PRIORITY.MEDIUM]: 'Medium',
  [JOB_PRIORITY.HIGH]: 'High',
} as const;

// ============================================================================
// File Types & Validation
// ============================================================================

export const ALLOWED_FILE_TYPES = {
  PDF: 'application/pdf',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
} as const;

export const ALLOWED_FILE_EXTENSIONS = ['.pdf', '.docx'] as const;

export const FILE_MAGIC_BYTES = {
  PDF: [0x25, 0x50, 0x44, 0x46], // %PDF
  DOCX: [0x50, 0x4B, 0x03, 0x04], // PK.. (ZIP format)
} as const;

export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes
export const MAX_CV_PAGES = 10;

// ============================================================================
// Processing Configuration
// ============================================================================

export const PROCESSING_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAYS: [1000, 4000, 16000], // Exponential backoff in ms
  CONCURRENT_WORKERS: 1,
  R2_POLLING_INTERVAL: 30000, // 30 seconds
  PROCESSING_TIMEOUT: 120000, // 2 minutes
} as const;

// ============================================================================
// Vector Database Configuration
// ============================================================================

export const VECTOR_CONFIG = {
  EMBEDDING_DIMENSIONS: 1024,
  DISTANCE_METRIC: 'cosine',
  CV_COLLECTION: 'cv_embeddings',
  JOB_COLLECTION: 'job_embeddings',
  TOP_K: 100,
} as const;

// ============================================================================
// Matching Configuration
// ============================================================================

export const MATCHING_WEIGHTS = {
  VECTOR_SIMILARITY: 0.6,
  SKILLS_MATCH: 0.2,
  EXPERIENCE_MATCH: 0.2,
} as const;

export const MATCH_SCORE_THRESHOLDS = {
  HIGH: 75,
  MEDIUM: 50,
  LOW: 0,
} as const;

// ============================================================================
// Search Configuration
// ============================================================================

export const SEARCH_CONFIG = {
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  CACHE_TTL: 300, // 5 minutes in seconds
  MATCH_CACHE_TTL: 3600, // 1 hour in seconds
} as const;

// ============================================================================
// Pagination
// ============================================================================

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,
} as const;

// ============================================================================
// Cache Keys
// ============================================================================

export const CACHE_KEYS = {
  MATCH: (jobId: string, cvId: string) => `match:${jobId}:${cvId}`,
  SEARCH: (queryHash: string) => `search:${queryHash}`,
  PRESIGNED_URL: (objectKey: string) => `presigned:${objectKey}`,
} as const;

// ============================================================================
// Presigned URL Configuration
// ============================================================================

export const PRESIGNED_URL_CONFIG = {
  UPLOAD_EXPIRY: 900, // 15 minutes in seconds
  DOWNLOAD_EXPIRY: 3600, // 1 hour in seconds
} as const;

// ============================================================================
// LLM Configuration
// ============================================================================

export const LLM_CONFIG = {
  MODEL: 'claude-3-5-haiku-20241022',
  MAX_TOKENS: 4096,
  TEMPERATURE: 0,
  STRUCTURED_OUTPUT: true,
} as const;

export const VOYAGE_CONFIG = {
  MODEL: 'voyage-2',
  INPUT_TYPE: 'document',
} as const;

// ============================================================================
// Backup Configuration
// ============================================================================

export const BACKUP_CONFIG = {
  SCHEDULE: '0 2 * * *', // Daily at 2 AM
  RETENTION_DAYS: 30,
  COMPRESSION: true,
} as const;

// ============================================================================
// Audit Actions
// ============================================================================

export const AUDIT_ACTIONS = {
  CV_UPLOAD: 'cv.upload',
  CV_DELETE: 'cv.delete',
  CV_VIEW: 'cv.view',
  CV_DOWNLOAD: 'cv.download',
  CV_RETRY: 'cv.retry',
  CV_ARCHIVE: 'cv.archive',
  JOB_CREATE: 'job.create',
  JOB_UPDATE: 'job.update',
  JOB_DELETE: 'job.delete',
  JOB_MATCH: 'job.match',
  SEARCH_EXECUTE: 'search.execute',
  SEARCH_EXPORT: 'search.export',
  USER_LOGIN: 'user.login',
  USER_LOGOUT: 'user.logout',
  USER_ROLE_CHANGE: 'user.role_change',
} as const;

// ============================================================================
// WebSocket Events
// ============================================================================

export const WS_EVENTS = {
  PROCESSING_STARTED: 'processing_started',
  PROCESSING_PROGRESS: 'processing_progress',
  PROCESSING_COMPLETED: 'processing_completed',
  PROCESSING_FAILED: 'processing_failed',
} as const;

// ============================================================================
// Export Formats
// ============================================================================

export const EXPORT_FORMATS = {
  CSV: 'csv',
  PDF: 'pdf',
  EXCEL: 'excel',
} as const;

// ============================================================================
// Error Types
// ============================================================================

export const ERROR_TYPES = {
  VALIDATION_ERROR: '/errors/validation-error',
  AUTHENTICATION_ERROR: '/errors/authentication-error',
  AUTHORIZATION_ERROR: '/errors/authorization-error',
  NOT_FOUND: '/errors/not-found',
  CV_PARSE_FAILED: '/errors/cv-parse-failed',
  CV_UPLOAD_FAILED: '/errors/cv-upload-failed',
  EMBEDDING_FAILED: '/errors/embedding-failed',
  SEARCH_FAILED: '/errors/search-failed',
  MATCH_FAILED: '/errors/match-failed',
  INTERNAL_ERROR: '/errors/internal-error',
} as const;

// ============================================================================
// HTTP Status Codes
// ============================================================================

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

// ============================================================================
// Date Formats
// ============================================================================

export const DATE_FORMATS = {
  DISPLAY: 'MMM d, yyyy',
  FULL: 'MMMM d, yyyy',
  SHORT: 'MM/dd/yyyy',
  ISO: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
} as const;

// ============================================================================
// Skill Experience Levels
// ============================================================================

export const EXPERIENCE_LEVELS = {
  JUNIOR: { min: 0, max: 2, label: 'Junior' },
  MID: { min: 2, max: 5, label: 'Mid-Level' },
  SENIOR: { min: 5, max: 10, label: 'Senior' },
  EXPERT: { min: 10, max: Infinity, label: 'Expert' },
} as const;

// ============================================================================
// Default Values
// ============================================================================

export const DEFAULTS = {
  RETRY_COUNT: 0,
  JOB_VERSION: 1,
  IS_ACTIVE: true,
  IS_ARCHIVED: false,
  PRIORITY: JOB_PRIORITY.MEDIUM,
} as const;

// ============================================================================
// Pipeline Stages
// ============================================================================

export const PIPELINE_STAGES = {
  APPLIED: 'applied',
  PHONE_SCREEN: 'phone_screen',
  INTERVIEW: 'interview',
  TECHNICAL: 'technical',
  FINAL: 'final',
  OFFER: 'offer',
  HIRED: 'hired',
  REJECTED: 'rejected',
} as const;

export const PIPELINE_STAGE_CONFIG = [
  { id: 'applied', label: 'Applied', color: 'gray', order: 0, icon: 'inbox' },
  { id: 'phone_screen', label: 'Phone Screen', color: 'blue', order: 1, icon: 'phone' },
  { id: 'interview', label: 'Interview', color: 'purple', order: 2, icon: 'users' },
  { id: 'technical', label: 'Technical', color: 'indigo', order: 3, icon: 'code' },
  { id: 'final', label: 'Final Round', color: 'orange', order: 4, icon: 'star' },
  { id: 'offer', label: 'Offer', color: 'green', order: 5, icon: 'file-text' },
  { id: 'hired', label: 'Hired', color: 'emerald', order: 6, icon: 'check-circle' },
  { id: 'rejected', label: 'Rejected', color: 'red', order: 7, icon: 'x-circle' },
] as const;

export const PIPELINE_STAGE_LABELS = {
  [PIPELINE_STAGES.APPLIED]: 'Applied',
  [PIPELINE_STAGES.PHONE_SCREEN]: 'Phone Screen',
  [PIPELINE_STAGES.INTERVIEW]: 'Interview',
  [PIPELINE_STAGES.TECHNICAL]: 'Technical',
  [PIPELINE_STAGES.FINAL]: 'Final Round',
  [PIPELINE_STAGES.OFFER]: 'Offer',
  [PIPELINE_STAGES.HIRED]: 'Hired',
  [PIPELINE_STAGES.REJECTED]: 'Rejected',
} as const;

// ============================================================================
// Interview Types
// ============================================================================

export const INTERVIEW_TYPES = {
  PHONE: 'phone',
  VIDEO: 'video',
  ONSITE: 'onsite',
} as const;

export const INTERVIEW_TYPE_LABELS = {
  [INTERVIEW_TYPES.PHONE]: 'Phone Call',
  [INTERVIEW_TYPES.VIDEO]: 'Video Call',
  [INTERVIEW_TYPES.ONSITE]: 'On-site',
} as const;

export const INTERVIEW_STATUS = {
  SCHEDULED: 'scheduled',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  NO_SHOW: 'no_show',
} as const;

export const INTERVIEW_STATUS_LABELS = {
  [INTERVIEW_STATUS.SCHEDULED]: 'Scheduled',
  [INTERVIEW_STATUS.CONFIRMED]: 'Confirmed',
  [INTERVIEW_STATUS.COMPLETED]: 'Completed',
  [INTERVIEW_STATUS.CANCELLED]: 'Cancelled',
  [INTERVIEW_STATUS.NO_SHOW]: 'No Show',
} as const;

export const INTERVIEW_RECOMMENDATIONS = {
  STRONG_YES: 'strong_yes',
  YES: 'yes',
  NEUTRAL: 'neutral',
  NO: 'no',
  STRONG_NO: 'strong_no',
} as const;

export const INTERVIEW_RECOMMENDATION_LABELS = {
  [INTERVIEW_RECOMMENDATIONS.STRONG_YES]: 'Strong Yes',
  [INTERVIEW_RECOMMENDATIONS.YES]: 'Yes',
  [INTERVIEW_RECOMMENDATIONS.NEUTRAL]: 'Neutral',
  [INTERVIEW_RECOMMENDATIONS.NO]: 'No',
  [INTERVIEW_RECOMMENDATIONS.STRONG_NO]: 'Strong No',
} as const;

// ============================================================================
// Email Template Types
// ============================================================================

export const EMAIL_TEMPLATE_TYPES = {
  INTERVIEW_INVITE: 'interview_invite',
  INTERVIEW_REMINDER: 'interview_reminder',
  INTERVIEW_FEEDBACK_REQUEST: 'interview_feedback_request',
  REJECTION: 'rejection',
  OFFER: 'offer',
  CUSTOM: 'custom',
} as const;

export const EMAIL_TEMPLATE_LABELS = {
  [EMAIL_TEMPLATE_TYPES.INTERVIEW_INVITE]: 'Interview Invitation',
  [EMAIL_TEMPLATE_TYPES.INTERVIEW_REMINDER]: 'Interview Reminder',
  [EMAIL_TEMPLATE_TYPES.INTERVIEW_FEEDBACK_REQUEST]: 'Feedback Request',
  [EMAIL_TEMPLATE_TYPES.REJECTION]: 'Rejection',
  [EMAIL_TEMPLATE_TYPES.OFFER]: 'Job Offer',
  [EMAIL_TEMPLATE_TYPES.CUSTOM]: 'Custom',
} as const;

export const EMAIL_STATUS = {
  SENT: 'sent',
  FAILED: 'failed',
  BOUNCED: 'bounced',
} as const;

// ============================================================================
// Pipeline Audit Actions
// ============================================================================

export const PIPELINE_AUDIT_ACTIONS = {
  PIPELINE_ADD: 'pipeline.add',
  PIPELINE_STAGE_CHANGE: 'pipeline.stage_change',
  PIPELINE_REJECT: 'pipeline.reject',
  PIPELINE_REMOVE: 'pipeline.remove',
  INTERVIEW_SCHEDULE: 'interview.schedule',
  INTERVIEW_UPDATE: 'interview.update',
  INTERVIEW_CANCEL: 'interview.cancel',
  INTERVIEW_FEEDBACK: 'interview.feedback',
  EMAIL_SEND: 'email.send',
} as const;

// ============================================================================
// Default Interview Durations
// ============================================================================

export const INTERVIEW_DURATIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '1 hour' },
  { value: 90, label: '1.5 hours' },
  { value: 120, label: '2 hours' },
] as const;
